import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Loader,
  LoadingOverlay,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import {
  IconCash,
  IconChevronLeft,
  IconFileDownload,
  IconInfoCircle,
  IconLink,
} from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';
import {
  mapBookingStatus,
  mapHolidayVariationStatus,
  mapPaymentStatus,
} from '@utils/mappers';

import env from '@config/env';

import {
  BookingPartecipant,
  CheckoutCosts,
} from '@interfaces/booking.interface';

import { useFindOneQuery, usePayBookingMutation } from '@api/bookings.api';
import { isApiError } from '@api/index';

import useQueryParams from '@hooks/queryParams/useQueryParams';

import PartecipantInfo from './PartecipantInfo';
import UpdatePartecipant, {
  UPDATE_PARTECIPANT_FORM_MODAL_ID,
} from './UpdatePartecipant';
import { priceToDecimal } from '@utils/price';
import {
  areParticipantsIncomplete,
  getBookingTotal,
  isFreeBooking,
  isPaymentSatisfied,
} from '@utils/booking';
import { useMediaQuery } from '@mantine/hooks';

export default function BookingDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [params, setParams] = useQueryParams();
  const idParam = useMemo(() => params.get('id'), [params]);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const [accomodationsIds, setAccomodationsIds] = useState<string[]>([]);

  const isMobile = useMediaQuery('(max-width: 36em)');

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleBackToList = () => {
    params.delete('id');
    setParams(params);
  };

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: bookingData, isLoading: isLoadingBookingData } =
    useFindOneQuery({ id: idParam! }, { skip: !idParam });

  const [payBooking] = usePayBookingMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleUpdatePartecipantClick = useCallback(
    (partecipant: BookingPartecipant) => {
      openModal({
        modalId: UPDATE_PARTECIPANT_FORM_MODAL_ID,
        title: t('bookings.detail.fields.participantData', {
          firstName: partecipant.firstName,
          lastName: partecipant.lastName,
        }),
        children: <UpdatePartecipant data={partecipant} />,
        size: 'lg',
      });
    },
    [],
  );

  const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const { checkoutUrl } = await payBooking(bookingData!.id).unwrap();

      location.href = checkoutUrl;
    } catch (e) {
      setIsLoading(false);

      //TODO remove this
      if (isApiError(e)) {
        openModal({
          title: t('common.validation.unexpectedError'),
          children: <Text c="red">{e.data.message}</Text>,
        });
      }
      console.error(e);
    }
  };

  const partecipantCosts =
    bookingData &&
    bookingData.partecipants &&
    bookingData.partecipants.map((partecipant) => {
      // Try new API structure first
      if (partecipant.holidayPrice !== null) {
        return {
          holidayCost: partecipant.holidayPrice ?? 0,
          partecipantId: partecipant.id,
          roomCost: partecipant.roomPrice ?? 0,
          extrasCosts: partecipant.optionPrice ?? 0,
          ensurancesCosts: partecipant.ensurancePrice ?? 0,
          couponValue: partecipant.coupon?.value ?? 0,
        };
      }

      // Fallback to old calculation
      const room = bookingData.rooms.find(
        (r) => r.id === partecipant.bookingRoomId,
      );

      return {
        holidayCost: bookingData.holidayPricePerPartecipant,
        partecipantId: partecipant.id,
        roomCost: room?.price || 0,
        extrasCosts: partecipant.bookingOptions.reduce(
          (sum, option) => sum + option!.price,
          0,
        ),
        ensurancesCosts: partecipant.bookingEnsurance?.price || 0,
      };
    });

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => `${bookingData?.holidayName} `,
    [bookingData?.holidayName],
  );

  const bookingCosts = useMemo(() => {
    if (!bookingData) return;

    // Try new API structure first
    if (bookingData.holidayPrice !== null) {
      return {
        holidayPrice: bookingData.holidayPrice ?? 0,
        roomsPrice: bookingData.roomsPrice ?? 0,
        extrasPrice: bookingData.optionsPrice ?? 0,
        ensurancesPrice: bookingData.ensurancesPrice ?? 0,
        couponValue: bookingData.couponsTotalValue ?? bookingData.couponValue,
      } as CheckoutCosts;
    }

    // Fallback to old calculation
    const holidayPrice =
      bookingData.holidayPricePerPartecipant * bookingData.partecipants.length;

    const roomsPrice = bookingData.rooms.reduce(
      (total, room) => total + room.price,
      0,
    );

    const extrasPrice = bookingData.partecipants.reduce(
      (total, partecipant) => {
        const participantExtra = partecipant.bookingOptions.reduce(
          (sum, option) => sum + option.price,
          0,
        );
        return total + participantExtra;
      },
      0,
    );

    const ensurancesPrice = bookingData.partecipants.reduce(
      (sum, option) => sum + (option.bookingEnsurance?.price || 0),
      0,
    );

    return {
      ensurancesPrice,
      extrasPrice,
      holidayPrice,
      roomsPrice,
      couponValue: bookingData.couponsTotalValue ?? bookingData.couponValue,
    } as CheckoutCosts;
  }, [bookingData]);

  const totalPrice = useMemo(() => {
    if (!bookingData) return 0;

    return getBookingTotal(bookingData);
  }, [bookingData]);

  const freeBooking = isFreeBooking(totalPrice);
  const paymentSatisfied = isPaymentSatisfied(
    totalPrice,
    bookingData?.totalPaid ?? 0,
  );

  // Fetch and validate accommodation IDs
  useEffect(() => {
    const fetchAccomodationsIds = async () => {
      if (!bookingData) {
        setAccomodationsIds([]);
        return;
      }

      const ids = new Set<string>();
      for (const room of bookingData.rooms) {
        if (room.accomodationId) {
          ids.add(room.accomodationId);
        }
      }

      const validIds = await Promise.all(
        Array.from(ids).filter(async (id) => {
          // Validate link
          const link = `${env.DOCUMENTS_URL}/${bookingData.holidayId}/${bookingData.holidayVariationId}/Infopartenza/${id}.pdf`;
          const res = await fetch(link);
          return res.ok;
        }),
      );

      setAccomodationsIds(validIds);
    };

    fetchAccomodationsIds();
  }, [bookingData]);

  const mappedPaymentStatus = mapPaymentStatus(
    bookingData?.totalPaid ?? 0,
    totalPrice,
    // Use paid status if there is at least one paid payment
    bookingData?.payments.some((payment) => payment.paymentStatus === 'paid')
      ? 'paid'
      : // Otherwise check the last payment status
        (bookingData?.payments.at(-1)?.paymentStatus ?? 'pending'), // We are assuming they are ordered by createdAt ASC
  );

  const hasOnePaidPayment = useMemo(() => {
    return bookingData?.payments.some(
      (payment) => payment.paymentStatus === 'paid',
    );
  }, [bookingData?.payments]);

  const maxTotalPaymentDate = useMemo(() => {
    // Holiday start date - 35 days
    if (!bookingData?.startDate) return null;

    return new Date(
      new Date(bookingData.startDate).getTime() - 35 * 24 * 60 * 60 * 1000,
    );
  }, [bookingData?.startDate]);

  return (
    <Container mt={{ base: 'md', xs: 0 }} p="0">
      {isLoadingBookingData ? (
        <Center>
          <Loader />
        </Center>
      ) : bookingData ? (
        <form onSubmit={handleConfirm}>
          <LoadingOverlay
            visible={isLoading}
            loaderProps={{
              children: (
                <Stack align="center" justify="center">
                  <Loader />
                  <Text fw="bold" size="lg">
                    {t('bookings.detail.verifying')}
                  </Text>
                </Stack>
              ),
            }}
          />
          <Box>
            <Button
              mb="md"
              variant="outline"
              onClick={handleBackToList}
              leftSection={<IconChevronLeft />}
            >
              {t('bookings.detail.backToList')}
            </Button>
            <Group>
              {bookingData.wordpressData && bookingData.wordpressData.image && (
                <Image
                  src={bookingData.wordpressData.image}
                  alt={bookingData.wordpressData.slug}
                  style={{
                    borderRadius: 'var(--mantine-radius-md) !important',
                  }}
                  h={150}
                />
              )}
              <Title c="#002d72">{title}</Title>
            </Group>
            {bookingData.bookingStatus === 'canceled' && (
              <Alert
                variant="light"
                color="red"
                title={t('bookings.detail.canceled.title')}
                icon={isMobile ? null : <IconInfoCircle />}
                my="xl"
              >
                <Text mb="md">{t('bookings.detail.canceled.message')}</Text>
                <Button
                  color="red"
                  onClick={() => {
                    window.open('https://www.jonas.it/contatti', '_blank');
                  }}
                >
                  {t('bookings.detail.canceled.contact')}
                </Button>
              </Alert>
            )}

            {bookingData.bookingStatus === 'suspended' && (
              <Alert
                variant="light"
                color="red"
                title={t('bookings.detail.suspended.title')}
                icon={isMobile ? null : <IconInfoCircle />}
                my="xl"
              >
                <Text mb="md">{t('bookings.detail.suspended.message')}</Text>
                <Button
                  color="red"
                  onClick={() => {
                    window.open('https://www.jonas.it/contatti', '_blank');
                  }}
                >
                  {t('bookings.detail.suspended.contact')}
                </Button>
              </Alert>
            )}

            {bookingData.bookingStatus !== 'canceled' &&
              bookingData.bookingStatus !== 'suspended' &&
              !freeBooking &&
              !paymentSatisfied && (
                <Alert
                  variant="light"
                  color="yellow"
                  title={mappedPaymentStatus.label}
                  icon={isMobile ? null : <IconInfoCircle />}
                  my="xl"
                >
                  <Text pb="md">
                    {bookingData.totalPaid > 0
                      ? t('bookings.detail.paymentPending.depositMessage', {
                          date: maxTotalPaymentDate
                            ? dateToDateString(maxTotalPaymentDate)
                            : '-',
                        })
                      : t('bookings.detail.paymentPending.message')}
                  </Text>
                  <Button
                    leftSection={<IconCash />}
                    type="submit"
                    color="yellow"
                  >
                    {t('bookings.detail.paymentPending.completePayment')}
                  </Button>
                </Alert>
              )}

            <Grid flex={1} gutter="xl">
              <Grid.Col span={{ md: 7 }}>
                {/* VACANZA */}
                <Title order={3}>
                  {t('bookings.detail.sections.holidaySummary')}
                </Title>
                <Divider my="md" />
                <Alert
                  variant="filled"
                  color={
                    mapHolidayVariationStatus(
                      bookingData.holidayVariation?.status ?? null,
                    ).color
                  }
                  title={`${t('bookings.list.columns.holiday')} ${mapHolidayVariationStatus(
                    bookingData.holidayVariation?.status ?? null,
                  ).label.toLocaleUpperCase()}`}
                  icon={
                    isMobile
                      ? null
                      : mapHolidayVariationStatus(
                          bookingData.holidayVariation?.status ?? null,
                        ).icon
                  }
                  mt="xs"
                  mb="md"
                />

                <Text c="#444">
                  <Text span fw="bold">
                    {t('bookings.detail.fields.period')}{' '}
                  </Text>
                  {bookingData.startDate && bookingData.endDate
                    ? `${dateToDateString(new Date(bookingData.startDate))} - ${dateToDateString(new Date(bookingData.endDate))}`
                    : '-'}
                </Text>
                {bookingData.wordpressData && (
                  <Button
                    mt="md"
                    variant="outline"
                    component="a"
                    href={`/vacanze/${bookingData.wordpressData.slug}`}
                    target="_blank"
                    leftSection={<IconLink />}
                  >
                    {t('bookings.detail.fields.program')}
                  </Button>
                )}
              </Grid.Col>

              <Grid.Col span={{ md: 5 }}>
                <Title order={3}>
                  {t('bookings.detail.sections.documents')}
                </Title>
                <Divider my="md" />

                <Group justify="space-between">
                  <Text fw="bold">
                    {t('bookings.detail.fields.precontract')}
                  </Text>
                  <Button
                    variant="outline"
                    size="xs"
                    leftSection={<IconFileDownload />}
                    component="a"
                    href={`${env.DOCUMENTS_URL}/${bookingData.holidayId}/Precontratto ${bookingData.holidayId}.pdf`}
                    target="_blank"
                  >
                    {t('bookings.detail.fields.download')}
                  </Button>
                </Group>

                {(hasOnePaidPayment || freeBooking) && (
                  <Group justify="space-between">
                    <Text fw="bold">
                      {t('bookings.detail.fields.contract')}
                    </Text>
                    <Button
                      variant="outline"
                      size="xs"
                      leftSection={<IconFileDownload />}
                      component="a"
                      href={`${env.DOCUMENTS_URL}/contratti/${bookingData.id}.pdf`}
                      target="_blank"
                    >
                      {t('bookings.detail.fields.download')}
                    </Button>
                  </Group>
                )}

                {paymentSatisfied &&
                  bookingData.bookingStatus === 'confirmed' &&
                  !areParticipantsIncomplete(bookingData) && (
                    <Group justify="space-between" mt="md">
                      <Text fw="bold">
                        {t('bookings.detail.fields.departureInfo')}
                      </Text>
                      <Button
                        variant="outline"
                        size="xs"
                        leftSection={<IconFileDownload />}
                        component="a"
                        href={`${env.DOCUMENTS_URL}/${bookingData.holidayId}/${bookingData.holidayVariationId}/Prepartenza ${bookingData.holidayVariationId}.pdf`}
                        target="_blank"
                      >
                        {t('bookings.detail.fields.download')}
                      </Button>
                    </Group>
                  )}

                {paymentSatisfied &&
                  bookingData.bookingStatus === 'confirmed' &&
                  !areParticipantsIncomplete(bookingData) &&
                  accomodationsIds.map((id) => (
                    <Group key={id} justify="space-between" mt="md">
                      <Text fw="bold">
                        {t(
                          'bookings.detail.fields.departureInfoByAccommodation',
                        )}
                      </Text>
                      <Button
                        variant="outline"
                        size="xs"
                        leftSection={<IconFileDownload />}
                        component="a"
                        href={`${env.DOCUMENTS_URL}/${bookingData.holidayId}/${bookingData.holidayVariationId}/Infopartenza/${id}.pdf`}
                        target="_blank"
                      >
                        {t('bookings.detail.fields.download')}
                      </Button>
                    </Group>
                  ))}
              </Grid.Col>

              {/* PRENOTAZIONE */}
              <Grid.Col span={{ md: 7 }}>
                <Title order={3}>
                  {t('bookings.detail.sections.bookingSummary')}
                </Title>

                <Divider my="md" />
                <Alert
                  variant="filled"
                  color={mapBookingStatus(bookingData.bookingStatus).color}
                  title={`${t('bookings.list.columns.bookingStatus')}: ${mapBookingStatus(
                    bookingData.bookingStatus,
                  ).label.toLocaleUpperCase()}
                    `}
                  icon={
                    isMobile
                      ? null
                      : mapBookingStatus(bookingData.bookingStatus).icon
                  }
                  mt="xs"
                  mb="md"
                ></Alert>

                {bookingData.partecipants.map((partecipant, index) => {
                  const addData =
                    bookingData.bookingStatus !== 'suspended' &&
                    (!partecipant.height ||
                      !partecipant.birthDate ||
                      !partecipant.birthCity ||
                      !partecipant.country ||
                      !partecipant.phone ||
                      (partecipant.country === 'Italia' &&
                        (!partecipant.province ||
                          !partecipant.city ||
                          !partecipant.zip ||
                          !partecipant.address ||
                          !partecipant.taxCode)) ||
                      !partecipant.email) &&
                    bookingData.bookingStatus !== 'canceled';

                  const partecipantCost = partecipantCosts!.find(
                    (c) => c.partecipantId === partecipant.id,
                  );

                  return (
                    <div key={index}>
                      <Group justify="space-between" align="center" mb="xs">
                        <Text key={index} fw="bold" fz="lg">
                          {partecipant.firstName} {partecipant.lastName}
                        </Text>
                        {addData && (
                          <Button
                            size="xs"
                            onClick={() => {
                              handleUpdatePartecipantClick(partecipant);
                            }}
                          >
                            {t('bookings.detail.fields.fillData')}
                          </Button>
                        )}
                      </Group>
                      <PartecipantInfo
                        partecipant={partecipant}
                        rooms={bookingData.rooms}
                        partecipantCosts={partecipantCost!}
                      />
                    </div>
                  );
                })}
              </Grid.Col>

              <Grid.Col span={{ md: 5 }}>
                <Title order={3}>
                  {t('bookings.detail.sections.costSummary')}
                </Title>
                <Divider my="md" />
                <Alert
                  variant="filled"
                  color={mappedPaymentStatus.color}
                  title={`${t('bookings.list.columns.paymentStatus')}:
                          ${mappedPaymentStatus.label.toLocaleUpperCase()}
                        `}
                  icon={isMobile ? null : mappedPaymentStatus.icon}
                  mt="xs"
                  mb="md"
                ></Alert>
                <Table
                  withRowBorders={false}
                  cellPadding={0}
                  cellSpacing={0}
                  c="#444"
                >
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td>
                        <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                          {t('bookings.detail.costs.holidayCost')}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                          € {priceToDecimal(bookingCosts!.holidayPrice)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    {bookingCosts!.roomsPrice !== 0 && (
                      <Table.Tr>
                        <Table.Td>
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            {t('bookings.detail.costs.roomCost')}
                          </Text>
                        </Table.Td>
                        <Table.Td align="right">
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            € {priceToDecimal(bookingCosts!.roomsPrice)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {bookingCosts!.extrasPrice !== 0 && (
                      <Table.Tr>
                        <Table.Td>
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            {t('bookings.detail.costs.extras')}
                          </Text>
                        </Table.Td>
                        <Table.Td align="right">
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            € {priceToDecimal(bookingCosts!.extrasPrice)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {bookingCosts!.ensurancesPrice !== 0 && (
                      <Table.Tr>
                        <Table.Td>
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            {t('bookings.detail.costs.insurance')}
                          </Text>
                        </Table.Td>
                        <Table.Td align="right">
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            € {priceToDecimal(bookingCosts!.ensurancesPrice)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {bookingCosts!.couponValue !== null &&
                      bookingCosts!.couponValue !== 0 && (
                        <Table.Tr>
                          <Table.Td>
                            <Text fs="italic" style={{ whiteSpace: 'nowrap' }}>
                              {t('bookings.detail.costs.discount')}
                            </Text>
                          </Table.Td>
                          <Table.Td align="right">
                            <Text fw="bold" style={{ whiteSpace: 'nowrap' }}>
                              - € {priceToDecimal(bookingCosts!.couponValue)}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    {bookingData.priceChanges?.map((priceChange) => (
                      <Table.Tr key={priceChange.id}>
                        <Table.Td>
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            {priceChange.notes}
                          </Text>
                        </Table.Td>
                        <Table.Td align="right">
                          <Text style={{ whiteSpace: 'nowrap' }} fs="italic">
                            € {priceToDecimal(priceChange.price)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ borderTop: '1px solid #E9ECEF' }}>
                      <Table.Td>
                        <Text>{t('bookings.detail.costs.total')}</Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <Text fw="bold">€ {priceToDecimal(totalPrice)}</Text>
                      </Table.Td>
                    </Table.Tr>
                    {bookingData.depositPrice &&
                      bookingData.depositPrice > 0 &&
                      bookingData.totalPaid > 0 && (
                        <>
                          <Table.Tr style={{ borderTop: '1px solid #E9ECEF' }}>
                            <Table.Td>
                              <Text>{t('bookings.detail.costs.deposit')}</Text>
                            </Table.Td>
                            <Table.Td align="right">
                              <Text>
                                € {priceToDecimal(bookingData.depositPrice)}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr style={{ borderTop: '1px solid #E9ECEF' }}>
                            <Table.Td>
                              <Text>{t('bookings.detail.costs.balance')}</Text>
                            </Table.Td>
                            <Table.Td align="right">
                              <Text>
                                €{' '}
                                {priceToDecimal(
                                  totalPrice - (bookingData.depositPrice ?? 0),
                                )}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        </>
                      )}
                  </Table.Tbody>
                </Table>
              </Grid.Col>
            </Grid>
          </Box>
        </form>
      ) : (
        <Stack align="center" justify="center" gap="xs">
          <Title>{t('bookings.detail.notFound', { id: idParam })}</Title>
          <Button onClick={handleBackToList}>
            {t('bookings.detail.backToList')}
          </Button>
        </Stack>
      )}
    </Container>
  );
}
